/**
 * API module responsible for handling campaign history-related requests.
 * All endpoints are scoped under `/campaigns`.
 */
import { createAPIConnection } from 'services';
import { requestHandler } from '../helpers';
import {
	GetCampaignsBoosterProductsRequest,
	GetCampaignsBoosterProductsResponse,
	GetCampaignsFiltersRequest,
	GetCampaignsFiltersResponse,
	GetCampaignsSummaryRequest,
	GetCampaignsSummaryResponse,
	GetIncentiveCampaignsFlagsResponse,
	GetCampaignGroupCnpjsRequest,
	GetCampaignGroupCnpjsResponse,
} from './interfaces';

const api = createAPIConnection('/campaigns');

/**
 * Fetches available filter options for campaign history.
 *
 * This endpoint is typically used to populate UI filters such as:
 * - Campaign dropdowns
 * - Date range pickers
 * - Status selectors
 *
 * It returns distinct values extracted from existing campaigns.
 *
 * @param params - Request parameters including filter criteria and POS identifier
 * @returns A promise resolving to available filter options
 *
 * @example
 * ```ts
 * const response = await getCampaignsFilters({
 *   posId: "pos-123",
 *   filter: {
 *     startDate: "2025-01-01",
 *     endDate: "2025-12-31"
 *   }
 * });
 *
 * console.log(response.data.campaignList);
 * ```
 */
export async function getCampaignsFilters({ filter, posId }: GetCampaignsFiltersRequest) {
	return requestHandler<GetCampaignsFiltersResponse>(api.post(`/${posId}/history/filters`, { filter }));
}

export async function getIncentiveCampaignsFlags({ posId }: { posId: POSEntity['id'] }) {
	return requestHandler<GetIncentiveCampaignsFlagsResponse>(api.get(`/info/flags?posId=${posId}`));
}

/**
 * Retrieves a summarized view of campaigns and their packages.
 *
 * This endpoint is designed for dashboards or reporting screens where
 * aggregated campaign performance is displayed.
 *
 * Supports filtering by:
 * - Campaign
 * - Date range
 * - Package payment status
 * - Package lifecycle status
 *
 * @param params - Request parameters including filters and POS identifier
 * @returns A promise resolving to summarized campaign data
 *
 * @example
 * ```ts
 * const summary = await getCampaignsSummary({
 *   posId: "pos-123",
 *   filter: {
 *     campaignId: "cmp-01",
 *     packageStatus: ["ACTIVE", "FINISHED"]
 *   }
 * });
 *
 * summary.data.campaignList.forEach(campaign => {
 *   console.log(campaign.campaignName);
 * });
 * ```
 */
export async function getCampaignsSummary({ filter, posId }: GetCampaignsSummaryRequest) {
	return requestHandler<GetCampaignsSummaryResponse>(api.post(`/${posId}/history/summary`, { filter }));
}

/**
 * Fetches booster products associated with a specific campaign package.
 *
 * Booster products are typically products that:
 * - Provide extra incentives
 * - Increase reward values
 * - Accelerate goal achievement within a campaign
 *
 * This endpoint is useful for:
 * - Detailed campaign views
 * - Product breakdown screens
 * - Incentive analysis
 *
 * @param params - Request parameters including campaign package ID and POS identifier
 * @returns A promise resolving to a list of booster products
 *
 * @example
 * ```ts
 * const boosters = await getCampaignsBoosterProducts({
 *   posId: "pos-123",
 *   campaignPackageId: "pkg-456"
 * });
 *
 * boosters.data.boosterProductList.forEach(product => {
 *   console.log(product.description, product.boosterValue);
 * });
 * ```
 */
export async function getCampaignsBoosterProducts({ campaignPackageId, posId }: GetCampaignsBoosterProductsRequest) {
	return requestHandler<GetCampaignsBoosterProductsResponse>(
		api.get(`/${posId}/history/${campaignPackageId}/booster-products`),
	);
}

export async function getCampaignGroupCnpjs({
	filter,
	packageId,
	page,
	posId,
	size = 20,
}: GetCampaignGroupCnpjsRequest) {
	return requestHandler<GetCampaignGroupCnpjsResponse>(
		api.post(
			`/packages/${packageId}/group`,
			{
				filter,
				page,
				size,
			},
			{
				params: {
					posId,
				},
			},
		),
	);
}
